'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { formatPercentual } from '@/app/relatorios/helpers/formatRelatorios';
import { IFaixasDeOcupacao } from '@/domain/configuracao/helpers/faixasOcupacao';
import { cn } from '@/lib/utils';

import { ICelulaDaVisao } from '../helpers/visaoExecutiva';
import { MAPA_POR_SEMANA_HREF, mapaDaCelulaHref } from '../helpers/visaoExecutivaLinks';
import { BlocoCard } from './BlocoCard';
import { EstadoDoBloco } from './EstadoDoBloco';
import { EixoDosMeses, FaixaDeSemanas } from './FaixaDeSemanas';
import { OcupacaoLegenda } from './OcupacaoLegenda';

/** O que separa as partes do resumo de uma célula. */
const SEPARADOR = ' · ';

/** O recuo lateral da linha. O eixo dos meses repete ele para alinhar com as colunas. */
const RECUO = 'px-2';

const LINHA = `flex w-full flex-col gap-1 rounded-md ${RECUO} py-1.5 text-left`;

interface CapacidadeBlocoProps {
  celulas: Array<ICelulaDaVisao>;
  /** Os cortes de cor em vigor (Configurações › Ocupação), os mesmos que o Mapa pinta. */
  faixas: IFaixasDeOcupacao;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * Quanto cada célula está cheia: o tamanho dela, a ocupação de hoje e como as semanas do
 * período se dividem entre folga, limite e estouro. A faixa é a grade do Mapa em
 * miniatura, coluna por coluna, com as MESMAS cores e os mesmos dois cortes de ocupação.
 *
 * A linha inteira é um atalho para o Mapa › Por semana filtrado naquela célula, que é onde
 * a resposta continua: aqui a pergunta é "cabe?", lá é "cabe o quê, em que semana".
 */
export function CapacidadeBloco({ celulas, faixas, loading, failed, onRetry, className }: CapacidadeBlocoProps) {
  const { t } = useTranslation('common');
  const router = useRouter();

  /**
   * O resumo de uma célula, dentro do período. A ocupação de hoje só entra quando o
   * período inclui a semana de hoje: num recorte futuro não há "hoje" para contar.
   */
  const resumoDaCelula = (celula: ICelulaDaVisao): string =>
    [
      celula.ocupacaoAtualPct === null
        ? null
        : t('visaoExecutiva.capacidade.hoje', { pct: formatPercentual(celula.ocupacaoAtualPct) }),
      t('visaoExecutiva.capacidade.comFolga', { n: celula.semanasComFolga }),
      t('visaoExecutiva.capacidade.noLimite', { n: celula.semanasNoLimite }),
      t('visaoExecutiva.capacidade.naoCabe', { n: celula.semanasNaoCabe }),
      // Semana sem carga é notícia só quando existe: um "0 sem carga" fixo é ruído.
      celula.semanasSemCarga > 0 ? t('visaoExecutiva.capacidade.semCarga', { n: celula.semanasSemCarga }) : null,
    ]
      .filter((parte): parte is string => parte !== null)
      .join(SEPARADOR);

  return (
    <BlocoCard
      pergunta={t('visaoExecutiva.capacidade.pergunta')}
      fonte={t('visaoExecutiva.capacidade.fonte')}
      acao={{ label: t('visaoExecutiva.capacidade.abrir'), href: MAPA_POR_SEMANA_HREF }}
      className={className}
    >
      <EstadoDoBloco
        loading={loading}
        failed={failed}
        vazio={celulas.length === 0}
        vazioTexto={t('visaoExecutiva.capacidade.vazio')}
        onRetry={onRetry}
      >
        {/* As faixas e o eixo dos meses dentro do MESMO contêiner que rola: um período
            longo (a carteira inteira) chega a mais colunas do que o cartão tem largura,
            e elas precisam rolar juntas para continuarem alinhadas. */}
        <div className="flex animate-fade-in flex-col gap-2 overflow-x-auto">
          <ul className="flex flex-col gap-2">
            {celulas.map((celula) => (
              <li key={celula.celula} className="flex min-w-0">
                <ControlledTooltip content={t('visaoExecutiva.capacidade.irParaCelula', { celula: celula.celula })}>
                  <ControlledButton
                    defaultHtmlButton
                    type="button"
                    onClick={() => router.push(mapaDaCelulaHref(celula.celula))}
                    className={cn(LINHA, 'interactive hover:bg-muted')}
                  >
                    <div className="flex w-full items-baseline justify-between gap-2">
                      <span className="flex min-w-0 items-baseline gap-1.5">
                        <span className="truncate text-xs font-semibold text-foreground">{celula.celula}</span>
                        {/* De quanta gente vem esta capacidade: sem isso não se sabe se a
                          semana é grande ou se é a célula que é pequena. */}
                        <span className="shrink-0 text-xs text-foreground-subtle">
                          {t('mapa.grade.pessoas', { count: celula.pessoas })}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-foreground-subtle">
                        {resumoDaCelula(celula)}
                      </span>
                    </div>

                    <FaixaDeSemanas semanas={celula.semanas} faixas={faixas} />
                  </ControlledButton>
                </ControlledTooltip>
              </li>
            ))}
          </ul>

          {/* Um eixo só para todas as células: as faixas têm as mesmas colunas, então o
              mês de uma é o mês de todas. */}
          <EixoDosMeses semanas={celulas[0]?.semanas ?? []} className={RECUO} />
        </div>

        <OcupacaoLegenda className="mt-auto" />
      </EstadoDoBloco>
    </BlocoCard>
  );
}
